import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Incident, IncidentFilters, IncidentList, IncidentsService, IncidentStatus, IncidentAssignee } from '../../../services/incidents/incidents.service';

@Component({
    selector: 'app-incidents', templateUrl: './incidents.component.html', styleUrls: ['./incidents.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class IncidentsComponent implements OnInit, OnDestroy {
  readonly statuses = [{ value: 'attention', label: 'Needs attention' }, { value: 'all', label: 'All' },
    { value: 'open', label: 'New' }, { value: 'in_progress', label: 'In progress' }, { value: 'done', label: 'Done' }, { value: 'wont_do', label: 'Won’t do' }];
  readonly columns = ['severity', 'title', 'lastEmailedAt', 'emailCount', 'owner', 'status', 'jira', 'actions'];
  filters: IncidentFilters = { page: 0, pageSize: 25, status: 'attention', owner: 'all', severity: '', service: '', search: '' };
  response?: IncidentList;
  incident?: Incident;
  id: string | null = null;
  loading = false;
  loadingMore = false;
  moreError = '';
  hasMore = false;
  private appliedFilters: IncidentFilters = { ...this.filters };
  @ViewChild('assignmentDialog', { static: true }) assignmentDialog!: TemplateRef<unknown>;
  private assignmentDialogRef?: MatDialogRef<unknown>;
  readonly assigneeSearch = new FormControl('', { nonNullable: true });
  get filteredAssignees(): IncidentAssignee[] {
    const search = this.assigneeSearch.value.trim().toLowerCase();
    return this.assignees.filter(person => `${person.name} ${person.email}`.toLowerCase().includes(search));
  }
  assignmentTarget?: Incident;
  assignees: IncidentAssignee[] = [];
  assigneeId = '';
  assigneesLoading = false;
  assigneesError = '';
  private assignmentRequest = 0;
  saving = false;
  error = '';
  saveError = '';
  fieldError = '';
  jiraDraft = '';
  noteDraft = '';
  takeoverPending = false;
  conflict = false;
  private routeSubscription?: Subscription;
  private request = 0;

  constructor(private readonly api: IncidentsService, private readonly route: ActivatedRoute, private readonly router: Router, private readonly dialog: MatDialog) {}
  ngOnInit(): void {
    this.routeSubscription = this.route.paramMap.subscribe(params => {
      this.id = params.get('incidentId');
      const query = this.route.snapshot.queryParamMap;
      for (const key of ['status', 'owner', 'severity', 'service', 'search']) {
        if (query.has(key)) (this.filters as any)[key] = query.get(key);
      }
      void this.load();
    });
  }
  ngOnDestroy(): void { this.request++; this.closeAssignment(); this.routeSubscription?.unsubscribe(); }
  async load(): Promise<void> {
    const request = ++this.request;
    this.loadingMore = false; this.moreError = ''; this.hasMore = false;
    this.filters.page = 0; this.appliedFilters = { ...this.filters };
    this.loading = true; this.error = ''; this.saveError = ''; this.fieldError = ''; this.conflict = false; this.takeoverPending = false;
    try {
      if (this.id) {
        const incident = await this.api.get(this.id);
        if (request !== this.request) return;
        this.incident = incident; this.jiraDraft = incident.jiraIssueKey || ''; this.noteDraft = incident.resolutionNote || '';
      } else {
        const response = await this.api.list({ ...this.appliedFilters });
        if (request !== this.request) return;
        this.response = response;
        this.hasMore = response.items.length > 0 && response.items.length < response.total;
      }
    } catch (error) { if (request === this.request) this.error = this.message(error, 'Incidents could not be loaded. Please try again.'); }
    finally { if (request === this.request) this.loading = false; }
  }
  applyFilters(): void { this.filters.page = 0; this.refreshList(); }
  get filterQueryParams(): object {
    const { status, owner, severity, service, search } = this.filters;
    return { status, owner, severity, service, search };
  }
  async loadMore(): Promise<void> {
    if (this.loading || this.loadingMore || this.saving || !this.hasMore || !this.response || this.id) return;
    const request = this.request;
    const nextPage = this.appliedFilters.page + 1;
    this.loadingMore = true; this.moreError = '';
    try {
      const response = await this.api.list({ ...this.appliedFilters, page: nextPage });
      if (request !== this.request) return;
      const existing = new Set(this.response.items.map(row => row.id));
      this.response = { ...response, items: [...this.response.items, ...response.items.filter(row => !existing.has(row.id))] };
      this.appliedFilters.page = nextPage;
      this.hasMore = response.items.length > 0 && (nextPage + 1) * this.appliedFilters.pageSize < response.total;
    } catch (error) {
      if (request === this.request) this.moreError = this.message(error, 'More incidents could not be loaded. Please retry.');
    } finally { if (request === this.request) this.loadingMore = false; }
  }
  async openAssignment(item: Incident): Promise<void> {
    const request = ++this.assignmentRequest;
    this.saveError = ''; this.fieldError = ''; this.conflict = false;
    this.assignmentTarget = item; this.assigneeId = ''; this.assignees = []; this.assigneesError = ''; this.assigneesLoading = true;
    this.assigneeSearch.setValue('');
    if (!this.assignmentDialogRef) {
      const ref = this.dialog.open(this.assignmentDialog, { width: '520px', maxWidth: '95vw' });
      this.assignmentDialogRef = ref;
      ref.afterClosed().subscribe(() => {
        if (this.assignmentDialogRef === ref) this.closeAssignment();
      });
    }
    try {
      const response = await this.api.assignees();
      if (request === this.assignmentRequest) this.assignees = response.items;
    } catch (error) { if (request === this.assignmentRequest) this.assigneesError = this.message(error, 'Assignees could not be loaded.'); }
    finally { if (request === this.assignmentRequest) this.assigneesLoading = false; }
  }
  closeAssignment(): void {
    const ref = this.assignmentDialogRef;
    this.assignmentDialogRef = undefined;
    this.assignmentRequest++; this.assignmentTarget = undefined; this.assigneesLoading = false;
    this.assigneeSearch.setValue('');
    ref?.close();
  }
  private refreshList(): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: this.filterQueryParams, replaceUrl: true });
    void this.load();
  }
  label(status: string): string { return this.statuses.find(item => item.value === status)?.label || status; }
  pretty(value: unknown): string { return JSON.stringify(value, null, 2); }
  async change(item: Incident, action: 'claim' | 'release' | 'takeover' | 'triage' | 'assign' | 'markDone', status?: IncidentStatus): Promise<void> {
    if (this.saving) return;
    if (this.assignmentDialogRef) this.assignmentDialogRef.disableClose = true;
    this.saving = true; this.saveError = ''; this.fieldError = ''; this.conflict = false;
    const changes = action === 'triage' ? {
      jiraIssueKey: this.jiraDraft, resolutionNote: this.noteDraft,
      ...(status ? { status, lastSeenAt: item.lastSeenAt } : {}),
    } : action === 'assign' ? { ownerUserId: this.assigneeId } : {};
    try {
      let updated: Incident;
      if (action === 'markDone') {
        let owned = item;
        if (item.actions.claim || item.actions.takeover) {
          owned = await this.api.mutate(item, item.actions.claim ? 'claim' : 'takeover');
          // Keep successful assignment visible even if closing subsequently fails.
          if (this.response) this.response.items = this.response.items.map(row => row.id === item.id ? owned : row);
          if (this.id) this.incident = owned;
        }
        // Use the new ownership version, but only acknowledge observations the user saw.
        updated = await this.api.mutate(owned, 'triage', { status: 'done', lastSeenAt: item.lastSeenAt });
      } else {
        updated = await this.api.mutate(item, action, changes);
      }
      if (this.id) {
        this.incident = updated;
        if (action === 'triage') { this.jiraDraft = updated.jiraIssueKey || ''; this.noteDraft = updated.resolutionNote || ''; }
      } else await this.load();
      this.takeoverPending = false;
      if (action === 'assign') this.closeAssignment();
    } catch (error) {
      const body = this.errorBody(error);
      this.saveError = this.message(error, 'The change could not be saved. Your draft is preserved.');
      this.fieldError = body.field || '';
      if (body.current) {
        this.conflict = true;
        if (this.assignmentTarget?.id === item.id) this.assignmentTarget = body.current;
        if (this.id) {
          this.incident = { ...this.incident!, ...body.current };
          // New observations must be reviewable before a close is retried. Keep form drafts.
          try { this.incident = await this.api.get(this.id); }
          catch (_) { this.error = 'Current incident details could not be loaded. Use Retry to load the current details.'; }
        }
        else if (this.response) this.response.items = this.response.items.map(row => row.id === item.id ? body.current : row);
      }
    } finally {
      this.saving = false;
      if (this.assignmentDialogRef) this.assignmentDialogRef.disableClose = false;
    }
  }
  private errorBody(error: any): any { return error?.data || error?.error || {}; }
  private message(error: any, defaultMessage: string): string {
    const body = this.errorBody(error);
    return typeof body.message === 'string' ? body.message : defaultMessage;
  }
}
